/*
 * 版权所有 (c) 华为技术有限公司 2024
 * Copyright (c) Huawei Technologies Co., Ltd. 2024. All rights reserved.
 */
import { Injectable } from '@angular/core';
import { type BaseRequestOptionsArgs, type HttpResponse, HttpService, TimeService } from '@dm/core';
import { DownloadService, ToolService } from 'app/common/services';
import { type Observable } from 'rxjs/internal/Observable';

import { type ExportLogParamItem, type PackageType, ExportNamespace } from './export.enum';

@Injectable()
export class ExportService {
    constructor(
        private httpService: HttpService,
        private downloadService: DownloadService,
        private time: TimeService,
        private tool: ToolService,
    ) { }

    /**
     * 获取控制器
     */
    getControllers(): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('controller.getBatch');
        return this.httpService.get(rest);
    }

    /**
     * 获取级联模块
     */
    getExpboards(): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('expboard.getBatch');
        return this.httpService.get(rest);
    }

    getDiskInfo(): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('disk.getBatch');
        return this.httpService.get(rest);
    }

    // 下发操作
    doAction(urlPath: string, param?: ExportLogParamItem, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl(urlPath);
        rest.setParams(param);
        return this.httpService.get(rest, { doException: false, ...options });
    }

    exportRunningData(param?: { [x: string]: any }, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl(ExportNamespace.runningData);
        rest.setParams(param);
        return this.httpService.get(rest, { doException: false, ...options });
    }

    exportEraseData(param?: { [x: string]: any; tag?: string }, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl(ExportNamespace.eraseLog);
        rest.setParams(param);
        return this.httpService.get(rest, { doException: false, ...options });
    }

    exportDiagnoseData(param?: { [x: string]: any; tag?: string }, options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl(ExportNamespace.diagnoseData);
        rest.setParams(param);
        return this.httpService.get(rest, { doException: false, ...options });
    }

    packagePerformanceData(param: { startTime: number; endTime: number; packageType: PackageType },
        options: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl(ExportNamespace.packagePerformanceFile, 'V2');
        return this.httpService.post(rest, { doException: false, ...options, ...param });
    }

    getPackagePerformanceDataStatus(options: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl(ExportNamespace.getPackagePerformanceFileStatus, 'V2');
        return this.httpService.get(rest, { doException: false, ...options });
    }

    downloadPerformanceData(param: { packageType: PackageType; languageFlag: string }): void {
        const rest = this.httpService.makeUrl(ExportNamespace.downloadPerformanceFile, 'V2');
        rest.setParams(param);
        this.downloadService.downloadFile(rest);
    }

    /**
     * 取消性能文件的导出
     */
    cancelExportPerfLog(options?: BaseRequestOptionsArgs): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('performanceData.cancelExport', 'V2');
        return this.httpService.delete(rest, options);
    }

    download(urlSpace: string, param: { [x: string]: string; tag?: string }): void {
        const rest = this.httpService.makeUrl(urlSpace);
        rest.setParams(param);
        this.downloadService.downloadFile(rest);
    }

    // 查询Pms Lun配置信息
    getPmsLun(): Observable<HttpResponse> {
        const rest = this.httpService.makeUrl('pmsLun.get', 'V2');
        return this.httpService.get(rest);
    }

    /**
     * 批量查询所有硬盘
     */
    getDisksBatch(options?: BaseRequestOptionsArgs): Observable<Disk[]> {
        const rest = this.httpService.makeUrl('disk.getBatch');
        return this.tool.getAllData(rest, options);
    }

    /**
     * 导出CSV文件
     */
    exportCsv(exportFileName: string, headers: string, contents: string): void {
        const csv = `\ufeff${headers}\n${contents}`;
        const blob = new Blob([csv], { type: 'text/csv; chartset=utf-8' });
        const time = this.time.formatUTC(this.time.currTime * 1000, 'YYYYMMDDHHmmss').replace(' DST', '');
        const fileName = `${exportFileName || 'export'}_${time}.csv`;

        this.downloadService.downloadWithBlob(blob, fileName);
    }
}
